import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, CalendarPlus, CalendarDays, Users, Map, Church, Eye, Tablet, BarChart3, FileDown, History, Settings, LogOut, Menu, X, Wifi, ClipboardCheck, ScrollText } from "lucide-react";
import { ParishMark } from "./common";
import { useAuth } from "../lib/auth";

const MENU = [
  ["/admin", "Dashboard", LayoutDashboard, ["admin", "officer"]],
  ["/admin/meetings/new", "Create Meeting", CalendarPlus, ["admin"]],
  ["/admin/meetings", "Manage Meetings", CalendarDays, ["admin", "officer"]],
  ["/admin/participants", "Participant Directory", Users, ["admin", "officer"]],
  ["/admin/gkk", "GKK Directory", Map, ["admin"]],
  ["/admin/ministries", "Ministry Directory", Church, ["admin"]],
  ["/admin/monitors", "Authorized Monitors", Eye, ["admin"]],
  ["/admin/devices", "Offline Devices", Tablet, ["admin"]],
  ["/station", "Offline Station", Wifi, ["admin", "officer"]],
  ["/admin/corrections", "Correction Requests", ClipboardCheck, ["admin"]],
  ["/admin/stats", "Statistics & Charts", BarChart3, ["admin", "officer"]],
  ["/admin/reports", "Reports & Exports", FileDown, ["admin", "officer"]],
  ["/admin/records", "Meeting Records", ScrollText, ["admin", "officer"]],
  ["/admin/audit", "Audit History", History, ["admin"]],
  ["/admin/settings", "System Settings", Settings, ["admin"]],
];

function Nav({ role, onNav }) {
  return (
    <nav className="flex flex-col gap-0.5">
      {MENU.filter((m) => m[3].includes(role)).map(([to, label, Icon]) => (
        <NavLink key={to} to={to} end={to === "/admin" || to === "/admin/meetings"} onClick={onNav}
          data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#2D3B2D] text-white" : "text-stone-700 hover:bg-[#EFE8D8]"}`}>
          <Icon className="h-4 w-4 shrink-0" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const out = () => { logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-[#FDFBF7] lg:grid lg:grid-cols-[272px_1fr]">
      <aside className="hidden lg:flex flex-col gap-6 border-r border-[#E5DEC9] bg-[#F7F4EE] p-5 sticky top-0 h-screen overflow-y-auto">
        <ParishMark small />
        <Nav role={user.role} />
        <UserBox user={user} out={out} />
      </aside>
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-[#E5DEC9] bg-[#F7F4EE]/95 backdrop-blur px-4 py-3">
        <ParishMark small />
        <button onClick={() => setOpen(true)} className="h-11 w-11 grid place-items-center rounded-xl border border-[#E5DEC9]" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[85%] bg-[#F7F4EE] p-5 flex flex-col gap-6 overflow-y-auto">
            <div className="flex justify-between items-center"><ParishMark small /><button onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X /></button></div>
            <Nav role={user.role} onNav={() => setOpen(false)} />
            <UserBox user={user} out={out} />
          </div>
        </div>
      )}
      <main className="min-w-0 px-4 py-6 sm:px-8 sm:py-10 max-w-[1500px]">
        <Outlet />
      </main>
    </div>
  );
}

const UserBox = ({ user, out }) => (
  <div className="mt-auto rounded-xl border border-[#E5DEC9] bg-white p-3">
    <div className="text-sm font-semibold text-stone-900 truncate" data-testid="current-user-name">{user.name}</div>
    <div className="text-xs text-stone-500 truncate">{user.email}</div>
    <div className="text-[10px] uppercase tracking-widest text-[#C85A32] font-bold mt-1">{user.role === "admin" ? "Administrator" : "Attendance Officer"}</div>
    <button onClick={out} className="mt-3 flex items-center gap-2 text-sm text-stone-600 hover:text-red-700" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
  </div>
);
